
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Lock, Save, Camera, Crown, Upload, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/errorMessage';
import { IMAGE_ACCEPT, storageUploadMessage, uploadErrorToast, validateImageFile, withImageType } from '@/lib/imageUpload';
import { PasswordChangeDialog } from './PasswordChangeDialog';

interface Profile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export const UserProfile: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    avatar_url: ''
  });

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          bio: data.bio || '',
          avatar_url: data.avatar_url || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const profileData = {
        user_id: user?.id,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        bio: formData.bio || null,
        avatar_url: formData.avatar_url || null,
        updated_at: new Date().toISOString()
      };

      if (profile) {
        const { error } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('user_id', user?.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('profiles')
          .insert(profileData);

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });

      loadProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: 'Invalid email', description: 'Enter a valid email address.', variant: 'destructive' });
      return;
    }
    if (email === (user?.email || '').toLowerCase()) {
      toast({ title: 'No change', description: "That's already your email address.", variant: 'destructive' });
      return;
    }

    setEmailSubmitting(true);
    try {
      // Supabase secure email change: a confirmation link is sent to BOTH the
      // current and the new address; the change only takes effect once confirmed.
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;

      toast({
        title: 'Confirmation sent',
        description:
          'Check both your current and new email and click the confirmation links to finish changing your address.',
        duration: 8000,
      });
      setNewEmail('');
      setShowEmailForm(false);
    } catch (error) {
      console.error('Error changing email:', error);
      toast({
        title: 'Could not change email',
        description: getErrorMessage(error) || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setEmailSubmitting(false);
    }
  };

  const getGravatarUrl = async (email: string, size: number = 80) => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(email.toLowerCase().trim());
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return `https://www.gravatar.com/avatar/${hashHex}?s=${size}&d=identicon`;
    } catch {
      // crypto.subtle unavailable (non-HTTPS context) — fall back to identicon
      return `https://www.gravatar.com/avatar/?s=${size}&d=identicon`;
    }
  };

  const [gravatarUrl, setGravatarUrl] = useState<string>('');

  useEffect(() => {
    if (user?.email) {
      getGravatarUrl(user.email).then(setGravatarUrl);
    }
  }, [user?.email]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !user) {
      input.value = '';
      return;
    }

    const problem = validateImageFile(file);
    if (problem) {
      input.value = '';
      toast({ ...uploadErrorToast(problem), variant: 'destructive' });
      return;
    }

    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, withImageType(file), { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(path);

      // Add cache buster to force refresh
      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      // Update profiles table
      if (profile) {
        await supabase
          .from('profiles')
          .update({ avatar_url: avatarUrl, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('profiles')
          .insert({ user_id: user.id, avatar_url: avatarUrl });
      }

      setFormData(prev => ({ ...prev, avatar_url: avatarUrl }));
      toast({ title: 'Profile photo updated' });
      loadProfile();
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast({ ...uploadErrorToast(storageUploadMessage(error)), variant: 'destructive' });
    } finally {
      setUploadingAvatar(false);
      input.value = '';
    }
  };

  const getDisplayName = () => {
    if (formData.first_name || formData.last_name) {
      return `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
    }
    return user?.email?.split('@')[0] || 'User';
  };

  const getInitials = () => {
    const name = getDisplayName();
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading profile...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <label className="cursor-pointer group">
                <input
                  type="file"
                  accept={IMAGE_ACCEPT}
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={uploadingAvatar}
                />
                <Avatar className="w-20 h-20 group-hover:opacity-80 transition-opacity">
                  <AvatarImage
                    src={formData.avatar_url || gravatarUrl}
                    alt="Profile picture"
                  />
                  <AvatarFallback className="text-lg">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 p-1 bg-background rounded-full border">
                  {uploadingAvatar ? (
                    <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </label>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold">{getDisplayName()}</h2>
                {isSubscribed && (
                  <Badge className="bg-yellow-500 text-white">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{user?.email}</span>
              </div>
              {formData.bio && (
                <p className="text-sm text-muted-foreground mt-2">{formData.bio}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div>
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <label>
                  <input
                    type="file"
                    accept={IMAGE_ACCEPT}
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploadingAvatar}
                  />
                  <Button variant="outline" size="sm" className="gap-1.5" asChild disabled={uploadingAvatar}>
                    <span>
                      {uploadingAvatar ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3" />
                      )}
                      {uploadingAvatar ? 'Uploading...' : formData.avatar_url ? 'Change Photo' : 'Upload Photo'}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground">
                  {formData.avatar_url ? 'Click to change your profile photo' : 'Or leave empty to use Gravatar'}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about yourself and your astrophotography interests..."
                rows={3}
              />
            </div>

            <Button type="submit" disabled={saving} className="w-full">
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Account Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Account Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Email Address</h4>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowEmailForm((v) => !v); setNewEmail(''); }}
                >
                  {showEmailForm ? 'Cancel' : 'Change Email'}
                </Button>
              </div>
              {showEmailForm && (
                <form onSubmit={handleEmailChange} className="mt-4 space-y-2">
                  <Label htmlFor="new_email">New email address</Label>
                  <Input
                    id="new_email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    We'll email a confirmation link to both your current and new address. The change takes effect once you confirm.
                  </p>
                  <Button type="submit" size="sm" disabled={emailSubmitting}>
                    {emailSubmitting ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send confirmation'
                    )}
                  </Button>
                </form>
              )}
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Password</h4>
                <p className="text-sm text-muted-foreground">Last updated: Not available</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowPasswordDialog(true)}
              >
                Change Password
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <PasswordChangeDialog
        isOpen={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
      />
    </div>
  );
};
